import React, { useMemo, useState } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { PageHeader } from '../../../shared/components/layout/PageHeader';
import { PageHeaderAction } from '../../../shared/components/layout/PageHeaderAction';
import { PageContentArea } from '../../../shared/components/layout/PageContentArea';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import { useGangLifecycleStore } from '../stores/gangLifecycleStore';
import { useGangManagementUIStore } from '../stores/gangManagementUIStore';
import { useGangsPanelStore } from '../stores/gangsPanelStore';
import { PermissionGate } from '@billda/permissions-ui';
import GangLifecyclePanel from '../components/templates/GangLifecyclePanel';
import GangDetailsPanel from '../components/templates/GangDetailsPanel';
import { GangsListView } from '../components/organisms/GangsListView';
import { useListGangs, useDeleteGang } from '../hooks/useGangHooks';
import { getErrorMessage } from '@billda/shared-ui';
import { mapGangToFormData } from '../utils/gangFormMappers';
import { GangCreationStep } from '../types/gangCreationTypes';
import { useUsers } from '../../users/hooks/useUsers';
import { ResizableHandle, SidePanel } from '../../../shared/components/panel';
import { HelpPanel } from '../../../shared/components/help/HelpPanel';
import { GANGS_HELP } from '../../../shared/constants/helpContent';

const GangManagementPage: React.FC = () => {
  const openCreateDrawer = useGangLifecycleStore(
    state => state.openCreateDrawer
  );
  const openEditDrawer = useGangLifecycleStore(state => state.openEditDrawer);
  const lifecycleMode = useGangLifecycleStore(state => state.mode);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Panel store for managing which panel is open
  const {
    isOpen: isPanelOpen,
    contentType,
    gangId: selectedGangId,
    openGangDetails,
    openLifecyclePanel,
    transitionToEdit,
    openHelp,
    closePanel,
  } = useGangsPanelStore();

  // UI store for search only
  const { searchText, setSearchText } = useGangManagementUIStore();

  const { data: gangs = [], isLoading: loading } = useListGangs();
  const { data: organisationUsers = [] } = useUsers();

  const deleteGangMutation = useDeleteGang();
  const isDeleting = deleteGangMutation.isPending;

  const handleDeleteGang = (gangId: string) => {
    deleteGangMutation.mutate(gangId, {
      onSuccess: () => {
        closePanel();
        setSuccessMessage('Gang deleted successfully.');
      },
      onError: (error: Error) => {
        setErrorMessage(getErrorMessage(error));
      },
    });
  };

  // Simple search filtering
  const filteredGangs = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return gangs;

    return gangs.filter(gang => {
      // Search gang name
      if (gang.name.toLowerCase().includes(query)) return true;

      // Search member names
      return gang.members.some(member => {
        const fullName =
          `${member.givenName ?? ''} ${member.familyName ?? ''}`.toLowerCase();
        return fullName.includes(query);
      });
    });
  }, [gangs, searchText]);

  const handleCreateGang = () => {
    openLifecyclePanel();
    openCreateDrawer();
  };

  const handleEditGang = (gangId: string, step: GangCreationStep) => {
    const gang = gangs.find(g => g.id === gangId);
    if (!gang) {
      setErrorMessage('Unable to load gang details for editing.');
      return;
    }

    const initialData = mapGangToFormData(gang, organisationUsers);
    transitionToEdit(gangId);
    openEditDrawer({
      gangId,
      initialData,
      completedSteps: [
        GangCreationStep.NAME,
        GangCreationStep.LEADERS,
        GangCreationStep.MEMBERS,
        GangCreationStep.MEMBER_CONFIG,
      ],
      activeStep: step ?? GangCreationStep.NAME,
    });
  };

  const selectedGang = selectedGangId
    ? gangs.find(g => g.id === selectedGangId) || null
    : null;

  const getPanelTitle = () => {
    if (contentType === 'gang-details') {
      return selectedGang?.name || 'Gang Details';
    }
    if (contentType === 'lifecycle') {
      return lifecycleMode === 'create' ? 'Create New Gang' : 'Edit Gang';
    }
    if (contentType === 'help') {
      return 'Help';
    }
    return 'Details';
  };

  return (
    <PageContentArea
      isPanelOpen={isPanelOpen}
      panel={
        isPanelOpen ? (
          <>
            <ResizableHandle />
            <PermissionGate permission="gangs.manage">
              <SidePanel
                open={isPanelOpen}
                onClose={closePanel}
                title={getPanelTitle()}
                testId={
                  contentType === 'gang-details'
                    ? 'gang-details-drawer'
                    : contentType === 'lifecycle'
                      ? 'gang-lifecycle-drawer'
                      : contentType === 'help'
                        ? 'gangs-help-drawer'
                        : undefined
                }
              >
                {contentType === 'gang-details' && (
                  <GangDetailsPanel
                    gang={selectedGang}
                    loading={loading}
                    onEdit={handleEditGang}
                    onDelete={handleDeleteGang}
                    isDeleting={isDeleting}
                  />
                )}
                {contentType === 'lifecycle' && (
                  <GangLifecyclePanel
                    onSuccess={setSuccessMessage}
                    onError={setErrorMessage}
                  />
                )}
                {contentType === 'help' && (
                  <HelpPanel helpContent={GANGS_HELP} />
                )}
              </SidePanel>
            </PermissionGate>
          </>
        ) : undefined
      }
    >
      <PageHeader
        title="Gang Management"
        helpAction={{
          onClick: openHelp,
          testId: 'gangs-help-button',
        }}
        actions={
          <PageHeaderAction
            label="Add Gang"
            onClick={handleCreateGang}
            icon={<AddIcon />}
            data-testid="add-gang-button"
          />
        }
      />

      <PageToolbar searchValue={searchText} onSearchChange={setSearchText} />

      <GangsListView
        gangs={filteredGangs}
        loading={loading}
        onRowClick={openGangDetails}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setErrorMessage(null)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </PageContentArea>
  );
};

export default GangManagementPage;
